import { Tr } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import type { OEMSettings } from '../../hooks/useGetSettings';
import { Icon, IconButton } from '../../ui';
import {
    SettingsAddItemsModal,
    SettingsNumberField,
    SettingsOverridesBlock,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    mergeEmptyOverrideRows,
    useEmptyOverrideRows,
    useSubTablePagination,
} from './primitives';

/** The per-make override fields this card is allowed to edit. */
export type OemOverrideField = 'drpThreshold' | 'nonDrpThreshold' | 'minQuotingTime';

export interface OemOverrideColumn {
    field: OemOverrideField;
    label: string;
    /** Stored value × scale is what the user sees; 100 for a 0–1 percentage. */
    scale: number;
    /** Range in displayed units, not stored units. */
    min: number;
    max: number;
    unit: string;
}

export interface OemOption {
    _id: string;
    name: string;
}

export interface OemOverridesTableProps {
    /** Sub-heading for the block, styled to match a SettingsRow label. */
    title: string;
    description: string;
    columns: Array<OemOverrideColumn>;
    /** The complete oem subdocument — overrides are stored on it. */
    oems: Array<OEMSettings>;
    oemOptions: Array<OemOption>;
    /** Receives the complete oem array; POST /settings/update replaces it wholesale. */
    onChange: (next: Array<OEMSettings>) => void;
    /** Body copy for the empty table. */
    emptyLabel: string;
    modalTitle: string;
    isDisabled?: boolean;
}

/**
 * True once an entry carries nothing but its make: no certifications, no
 * rebate, no overrides. Rows the user adds here create an oem entry, so
 * clearing the last override has to take that entry back out rather than leave
 * a bare make sitting in the OEM list.
 */
const isBareOemEntry = (oem: OEMSettings) =>
    oem.drpThreshold == null &&
    oem.nonDrpThreshold == null &&
    oem.minQuotingTime == null &&
    !oem.rebate &&
    oem.certifications !== 'all' &&
    (oem.certifications?.length ?? 0) === 0;

/**
 * Per-make overrides of a global setting, as a table.
 *
 * The values live on the `oem` subdocument — the same fields the OEM editor
 * used to own — but they belong next to the global setting they override, so
 * this renders them under Quoting and Ordering instead. Nothing about the
 * stored shape changes: a row is an `OEMSettings` entry with one or more of
 * `drpThreshold`, `nonDrpThreshold`, `minQuotingTime` set.
 *
 * This is a block inside a SettingsCard, not a card of its own: the overrides
 * share one box with the global setting they override, sitting below its rows
 * behind the card's own divider.
 */
const OemOverridesTable = ({
    title,
    description,
    columns,
    oems,
    oemOptions,
    onChange,
    emptyLabel,
    modalTitle,
    isDisabled = false,
}: OemOverridesTableProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalSearchTerm, setModalSearchTerm] = useState('');
    const [pendingMakeIds, setPendingMakeIds] = useState<Set<string>>(new Set());
    // Makes added from the picker stay listed until they are given a value,
    // otherwise the new row would vanish the moment it rendered.
    const emptyRows = useEmptyOverrideRows<string>();

    const rows = useMemo(() => {
        const byMakeId = new Map(oems.map((o) => [o.makeId, o]));
        const listed = oems.filter((oem) => columns.some((column) => oem[column.field] != null));

        return mergeEmptyOverrideRows(
            listed,
            emptyRows.emptyRowIds,
            (oem) => oem.makeId,
            (makeId) => {
                const existing = byMakeId.get(makeId);
                if (existing) return existing;
                const option = oemOptions.find((o) => o._id === makeId);
                return option
                    ? {
                          makeId,
                          name: option.name,
                          certifications: [],
                          drpThreshold: null,
                          nonDrpThreshold: null,
                          minQuotingTime: null,
                      }
                    : null;
            },
        ).sort((a, b) => a.name.localeCompare(b.name));
    }, [oems, emptyRows.emptyRowIds, oemOptions, columns]);

    const paging = useSubTablePagination(rows);

    const availableMakes = useMemo(() => {
        const takenIds = new Set(rows.map((r) => r.makeId));
        return oemOptions
            .filter((o) => o.name && o.name.trim() !== '')
            .filter((o) => !takenIds.has(o._id))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [oemOptions, rows]);

    const filteredModalMakes = useMemo(() => {
        const search = modalSearchTerm.toLowerCase();
        return availableMakes.filter((m) => m.name.toLowerCase().includes(search));
    }, [availableMakes, modalSearchTerm]);

    const openModal = () => {
        setModalSearchTerm('');
        setPendingMakeIds(new Set());
        setIsModalOpen(true);
    };

    const togglePendingMake = (makeId: string) => {
        setPendingMakeIds((prev) => {
            const next = new Set(prev);
            if (next.has(makeId)) next.delete(makeId);
            else next.add(makeId);
            return next;
        });
    };

    const handleAddMakes = () => {
        if (pendingMakeIds.size === 0) return;
        emptyRows.addEmptyRows(pendingMakeIds);
        setIsModalOpen(false);
    };

    /** Write one field on one make, creating or pruning the entry as needed. */
    const handleFieldChange = (row: OEMSettings, field: OemOverrideField, next: number | null) => {
        // Clearing the last value would otherwise prune the entry and drop the
        // row out from under the cursor; keep it listed until it is removed.
        if (next == null && !emptyRows.hasEmptyRow(row.makeId)) {
            emptyRows.addEmptyRows([row.makeId]);
        }

        const existing = oems.find((o) => o.makeId === row.makeId);

        if (!existing) {
            if (next == null) return;
            onChange([...oems, { ...row, [field]: next }]);
            return;
        }

        const updated = { ...existing, [field]: next };
        onChange(
            isBareOemEntry(updated)
                ? oems.filter((o) => o.makeId !== row.makeId)
                : oems.map((o) => (o.makeId === row.makeId ? updated : o)),
        );
    };

    const handleRemoveRow = (row: OEMSettings) => {
        emptyRows.removeEmptyRow(row.makeId);

        const existing = oems.find((o) => o.makeId === row.makeId);
        if (!existing) return;

        const cleared = { ...existing };
        columns.forEach((column) => {
            cleared[column.field] = null;
        });

        onChange(
            isBareOemEntry(cleared)
                ? oems.filter((o) => o.makeId !== row.makeId)
                : oems.map((o) => (o.makeId === row.makeId ? cleared : o)),
        );
    };

    return (
        <>
            <SettingsOverridesBlock
                title={title}
                description={description}
                addButtonText="Add Make"
                onAddClick={openModal}
                isDisabled={isDisabled}
            >
                <SettingsSubTable
                    isInset
                    currentPage={paging.currentPage}
                    totalPages={paging.totalPages}
                    onPageChange={paging.setCurrentPage}
                    head={
                        <>
                            <SettingsTh>Make</SettingsTh>
                            {columns.map((column) => (
                                <SettingsTh key={column.field}>{column.label}</SettingsTh>
                            ))}
                            <SettingsTh textAlign="center" w="100px">
                                Actions
                            </SettingsTh>
                        </>
                    }
                >
                    {rows.length === 0 ? (
                        <Tr>
                            <SettingsTd colSpan={columns.length + 2} py={6} color="gray.500">
                                {emptyLabel}
                            </SettingsTd>
                        </Tr>
                    ) : (
                        paging.pageItems.map((row) => (
                            <Tr key={row.makeId}>
                                <SettingsTd fontWeight="medium" color="gray.800">
                                    {row.name}
                                </SettingsTd>
                                {columns.map((column) => {
                                    const stored = row[column.field];
                                    return (
                                        <SettingsTd key={column.field}>
                                            <SettingsNumberField
                                                value={
                                                    stored == null
                                                        ? null
                                                        : Math.round(stored * column.scale)
                                                }
                                                onChange={(next) =>
                                                    handleFieldChange(
                                                        row,
                                                        column.field,
                                                        next == null ? null : next / column.scale,
                                                    )
                                                }
                                                min={column.min}
                                                max={column.max}
                                                width="90px"
                                                unit={column.unit}
                                                placeholder="None"
                                                ariaLabel={`${row.name} ${column.label}`}
                                                isDisabled={isDisabled}
                                            />
                                        </SettingsTd>
                                    );
                                })}
                                <SettingsTd textAlign="center">
                                    <IconButton
                                        aria-label={`Remove ${row.name}`}
                                        icon={<Icon name="trash" size="sm" />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={() => handleRemoveRow(row)}
                                        isDisabled={isDisabled}
                                    />
                                </SettingsTd>
                            </Tr>
                        ))
                    )}
                </SettingsSubTable>
            </SettingsOverridesBlock>

            <SettingsAddItemsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                modalTitle={modalTitle}
                searchPlaceholder="Search makes..."
                searchTerm={modalSearchTerm}
                onSearchTermChange={setModalSearchTerm}
                emptySearchLabel={modalSearchTerm ? 'No makes found' : 'All makes already listed'}
                items={filteredModalMakes.map((make) => ({
                    id: make._id,
                    label: make.name,
                }))}
                pendingIds={pendingMakeIds}
                onTogglePending={togglePendingMake}
                selectedCountLabel={
                    pendingMakeIds.size > 0
                        ? `${pendingMakeIds.size} make${pendingMakeIds.size > 1 ? 's' : ''} selected`
                        : null
                }
                onAdd={handleAddMakes}
            />
        </>
    );
};

export default OemOverridesTable;
